// Layout JSON → struct-of-arrays Grid, plus validation for content authors.

import type { Grid, Layout, Prop, ZoneId, ZoneRule } from './types';
import { CELL_TYPES, GRASS_TYPES, ZONE_IDS } from './types';
import { mulberry32, type Rng } from './rng';

// Indices into CELL_TYPES (kept in sync by the assertion below).
export const T_GRASS = 0;
export const T_PAVED = 1;
export const T_BED = 2;
export const T_OBSTACLE = 3;
export const T_EDGE = 4;
export const T_WATER = 5;
export const T_OUT = 6;
if (CELL_TYPES[T_OUT] !== 'out' || CELL_TYPES[T_OBSTACLE] !== 'obstacle') throw new Error('CELL_TYPES order changed');

export const G_COUCH = 0;
export const G_BUFFALO = 1;
export const G_KIKUYU = 2;
export const G_WEEDY = 3;

const ROW_CHARS: Record<string, number> = { g: T_GRASS, G: T_GRASS, p: T_PAVED, b: T_BED, o: T_OBSTACLE, e: T_EDGE, w: T_WATER, '.': T_OUT };
const ZONE_CHARS: Record<string, ZoneId> = { '.': 'none', W: 'wicket-square', P: 'pitch', G: 'green', O: 'outfield', N: 'nature-strip', D: 'dam-edge' };

/**
 * The sim's grid: the frozen `Grid` plus derived per-cell data. Renderers may
 * read `trim` (pro-vision) but should treat everything here as read-only.
 */
export interface SimGrid extends Grid {
  /** 1 = counts toward mow %: grass, not trim, not do-not-mow. */
  mowable: Uint8Array;
  /** 1 = do-not-mow zone cell. */
  dnm: Uint8Array;
  /** Target cut range per cell (mm). */
  lo: Float32Array;
  hi: Float32Array;
  /** Index of the solid prop rasterised onto this cell (an obstacle cell, or a small prop's snip cell), or −1. */
  propAt: Int16Array;
  /** Per ZONE_IDS index: 1 = blades in this do-not-mow zone are fined (ZoneRule.penalty, default true). */
  zoneFine: Uint8Array;
  mowableCount: number;
  trimCount: number;
  /** Weeds at build time. */
  weedCount: number;
  /** Long side (cells) of the bounding box of the mowable + trim cells. */
  spanCells: number;
}

/** True for cell types nothing can pass through (mower, walker, leaves). */
export function isSolidType(t: number): boolean {
  return t === T_OBSTACLE || t === T_EDGE || t === T_WATER || t === T_OUT;
}

/** Types that make a neighbouring grass cell a trim cell. */
function blocksMower(t: number): boolean {
  return t === T_BED || t === T_OBSTACLE || t === T_EDGE || t === T_WATER || t === T_OUT;
}

export function zoneRuleMap(layout: Layout): Array<ZoneRule | undefined> {
  const out: Array<ZoneRule | undefined> = ZONE_IDS.map(() => undefined);
  for (const z of layout.zones) {
    const i = ZONE_IDS.indexOf(z.id);
    if (i >= 0) out[i] = z;
  }
  return out;
}

/** Does world point (px,py) lie inside the prop's footprint? */
export function pointInProp(p: Prop, px: number, py: number): boolean {
  const dx = px - p.x;
  const dy = py - p.y;
  if (p.r !== undefined) return dx * dx + dy * dy <= p.r * p.r;
  const rot = p.rot ?? 0;
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  const lx = dx * c + dy * s;
  const ly = -dx * s + dy * c;
  return Math.abs(lx) <= (p.w ?? 0.5) / 2 && Math.abs(ly) <= (p.h ?? 0.5) / 2;
}

/** Axis-aligned bounding half-extents of a prop's footprint. */
export function propHalfExtents(p: Prop): [number, number] {
  if (p.r !== undefined) return [p.r, p.r];
  const hw = (p.w ?? 0.5) / 2;
  const hh = (p.h ?? 0.5) / 2;
  const rot = p.rot ?? 0;
  const c = Math.abs(Math.cos(rot));
  const s = Math.abs(Math.sin(rot));
  return [hw * c + hh * s, hw * s + hh * c];
}

/**
 * A solid prop at most half a cell across (goal post, junior post). It is not
 * rasterised as an obstacle cell: it collides by its true footprint only.
 * scripts/layouts/generate.mjs applies the same rule when stamping rows.
 */
export function isSmallProp(p: Prop, cellM: number): boolean {
  const [ex, ey] = propHalfExtents(p);
  return 2 * Math.max(ex, ey) <= cellM / 2;
}

/** Deck step for a layout: a zone rule's step, 2 mm on bowls/wicket layouts, else 10. */
export function deckStepFor(layout: Layout): number {
  let step = Infinity;
  let fine = false;
  for (const z of layout.zones) {
    if (z.deckStepMm !== undefined) step = Math.min(step, z.deckStepMm);
    if (z.id === 'green' || z.id === 'wicket-square') fine = true;
  }
  if (step !== Infinity) return step;
  return fine ? 2 : 10;
}

/**
 * Parse a layout into a grid. `rng` draws the per-cell start lengths and the
 * paved weeds (grass cells first, row-major, then weeds) so it is deterministic.
 */
export function buildGrid(layout: Layout, rng: Rng): SimGrid {
  const w = layout.w;
  const h = layout.h;
  const n = w * h;
  const cellM = layout.cellM;
  const type = new Uint8Array(n).fill(T_OUT);
  const zone = new Uint8Array(n);
  const grass = new Uint8Array(n);
  const lengthMm = new Float32Array(n);
  const startLengthMm = new Float32Array(n);
  const trim = new Uint8Array(n);
  const mowable = new Uint8Array(n);
  const dnm = new Uint8Array(n);
  const lo = new Float32Array(n);
  const hi = new Float32Array(n);
  const propAt = new Int16Array(n).fill(-1);
  const weed = new Uint8Array(n);
  const baseGrass = Math.max(0, GRASS_TYPES.indexOf(layout.grassType));

  for (let y = 0; y < h; y++) {
    const row = layout.rows[y] ?? '';
    const zrow = layout.zoneRows?.[y];
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const ch = row[x] ?? '.';
      type[i] = ROW_CHARS[ch] ?? T_OUT;
      grass[i] = ch === 'G' ? G_WEEDY : baseGrass;
      if (zrow) {
        const zid = ZONE_CHARS[zrow[x] ?? '.'] ?? 'none';
        zone[i] = ZONE_IDS.indexOf(zid);
      }
    }
  }

  // Solid props become obstacle cells (cell centre inside the footprint, and
  // always the cell under the prop's centre) — unless the prop is SMALL (at
  // most half a cell across, e.g. a 0.4 m goal post on 1.5 m oval cells).
  // A small prop collides by its true circle/rect only (mower.ts): the cells
  // its footprint touches keep their type, and they and their neighbours are
  // trim cells, as next to any obstacle (you snip around a goal post; no deck
  // gets that close anyway).
  const snip = new Uint8Array(n);
  layout.props.forEach((p, pi) => {
    if (!p.solid) return;
    const [ex, ey] = propHalfExtents(p);
    const x0 = Math.max(0, Math.floor((p.x - ex) / cellM));
    const x1 = Math.min(w - 1, Math.floor((p.x + ex) / cellM));
    const y0 = Math.max(0, Math.floor((p.y - ey) / cellM));
    const y1 = Math.min(h - 1, Math.floor((p.y + ey) / cellM));
    if (isSmallProp(p, cellM)) {
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const i = y * w + x;
          propAt[i] = pi;
          snip[i] = 1;
        }
      }
      return;
    }
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        if (pointInProp(p, (x + 0.5) * cellM, (y + 0.5) * cellM)) {
          const i = y * w + x;
          if (type[i] !== T_OUT) type[i] = T_OBSTACLE;
          propAt[i] = pi;
        }
      }
    }
    const cx = Math.floor(p.x / cellM);
    const cy = Math.floor(p.y / cellM);
    if (cx >= 0 && cy >= 0 && cx < w && cy < h) {
      const i = cy * w + cx;
      if (type[i] !== T_OUT) type[i] = T_OBSTACLE;
      propAt[i] = pi;
    }
  });

  const rules = zoneRuleMap(layout);
  let mowableCount = 0;
  let trimCount = 0;
  let bx0 = w, bx1 = -1, by0 = h, by1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (type[i] !== T_GRASS) continue;
      const rule = rules[zone[i]];
      const range = rule?.rangeMm ?? layout.rangeMm;
      lo[i] = range[0];
      hi[i] = range[1];
      const len = Math.round(layout.startLengthMm[0] + (layout.startLengthMm[1] - layout.startLengthMm[0]) * rng());
      lengthMm[i] = len;
      startLengthMm[i] = len;
      if (rule?.doNotMow) {
        dnm[i] = 1;
        continue;
      }
      let edge = snip[i] === 1;
      for (let dy = -1; dy <= 1 && !edge; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h || blocksMower(type[ny * w + nx]) || snip[ny * w + nx]) {
            edge = true;
            break;
          }
        }
      }
      if (edge) {
        trim[i] = 1;
        trimCount++;
      } else {
        mowable[i] = 1;
        mowableCount++;
      }
      if (x < bx0) bx0 = x;
      if (x > bx1) bx1 = x;
      if (y < by0) by0 = y;
      if (y > by1) by1 = y;
    }
  }

  let weedCount = 0;
  if (layout.weedFraction > 0) {
    for (let i = 0; i < n; i++) {
      if (type[i] === T_PAVED && rng() < layout.weedFraction) {
        weed[i] = 1;
        weedCount++;
      }
    }
  }

  const zoneFine = new Uint8Array(ZONE_IDS.length);
  rules.forEach((r, zi) => {
    zoneFine[zi] = r?.doNotMow && r.penalty !== false ? 1 : 0;
  });

  return {
    w, h, cellM, type, zone, grass, lengthMm, startLengthMm,
    cut: new Uint8Array(n), stripe: new Uint8Array(n), weed, wet: new Uint8Array(n), trim, dirty: [],
    mowable, dnm, lo, hi, propAt, zoneFine, mowableCount, trimCount, weedCount,
    spanCells: bx1 < 0 ? 0 : Math.max(bx1 - bx0 + 1, by1 - by0 + 1),
  };
}

/** Cell index at world (x,y) metres, or −1 outside the grid. */
export function cellAt(g: Grid, x: number, y: number): number {
  const cx = Math.floor(x / g.cellM);
  const cy = Math.floor(y / g.cellM);
  if (cx < 0 || cy < 0 || cx >= g.w || cy >= g.h) return -1;
  return cy * g.w + cx;
}

export interface LayoutStats {
  grassCells: number;
  mowableCells: number;
  trimCells: number;
  dnmCells: number;
  pavedCells: number;
  /** Long side (cells) of the lawn's bounding box (SimGrid.spanCells). */
  spanCells: number;
  /** (mowable + trim) × cellM² — the billable lawn. */
  areaM2: number;
  solidProps: number;
  penaltyProps: number;
}

const statsCache = new WeakMap<Layout, LayoutStats>();

/** Cell counts for fees, complexity and par (cached per layout object). */
export function layoutStats(layout: Layout): LayoutStats {
  const hit = statsCache.get(layout);
  if (hit) return hit;
  const g = buildGrid(layout, mulberry32(0));
  let grassCells = 0;
  let dnmCells = 0;
  let pavedCells = 0;
  for (let i = 0; i < g.type.length; i++) {
    if (g.type[i] === T_GRASS) grassCells++;
    if (g.dnm[i]) dnmCells++;
    if (g.type[i] === T_PAVED) pavedCells++;
  }
  const solid = layout.props.filter((p) => p.solid);
  const stats: LayoutStats = {
    grassCells,
    mowableCells: g.mowableCount,
    trimCells: g.trimCount,
    dnmCells,
    pavedCells,
    spanCells: g.spanCells,
    areaM2: (g.mowableCount + g.trimCount) * layout.cellM * layout.cellM,
    solidProps: solid.length,
    penaltyProps: solid.filter((p) => p.penalty !== false).length,
  };
  statsCache.set(layout, stats);
  return stats;
}

/** Content checks. Returns human-readable errors; empty = valid. */
export function validateLayout(layout: Layout): string[] {
  const errs: string[] = [];
  const { w, h, cellM } = layout;
  if (!layout.key) errs.push('missing key');
  if (!Number.isInteger(w) || w <= 0 || !Number.isInteger(h) || h <= 0) errs.push(`bad size ${w}×${h}`);
  if (!(cellM > 0)) errs.push(`bad cellM ${cellM}`);
  if (layout.gameScale !== undefined && !(layout.gameScale > 0)) errs.push(`bad gameScale ${layout.gameScale}`);
  if (!Array.isArray(layout.rows) || layout.rows.length !== h) errs.push(`rows: expected ${h}, got ${layout.rows?.length}`);
  layout.rows?.forEach((r, y) => {
    if (r.length !== w) errs.push(`row ${y}: length ${r.length}, expected ${w}`);
    for (let x = 0; x < r.length; x++) {
      if (!(r[x] in ROW_CHARS)) {
        errs.push(`row ${y} col ${x}: bad char '${r[x]}'`);
        break;
      }
    }
  });
  const zoneIds = new Set<ZoneId>();
  for (const z of layout.zones ?? []) {
    if (!ZONE_IDS.includes(z.id)) errs.push(`zone rule: unknown id '${z.id}'`);
    zoneIds.add(z.id);
    if (!z.doNotMow) {
      if (!z.rangeMm || z.rangeMm.length !== 2 || !(z.rangeMm[0] < z.rangeMm[1])) errs.push(`zone ${z.id}: needs rangeMm [lo, hi] unless doNotMow`);
    }
    if (z.deckStepMm !== undefined && !(z.deckStepMm > 0)) errs.push(`zone ${z.id}: bad deckStepMm`);
  }
  if (layout.zoneRows) {
    if (layout.zoneRows.length !== h) errs.push(`zoneRows: expected ${h}, got ${layout.zoneRows.length}`);
    const used = new Set<ZoneId>();
    layout.zoneRows.forEach((r, y) => {
      if (r.length !== w) errs.push(`zoneRow ${y}: length ${r.length}, expected ${w}`);
      for (let x = 0; x < r.length; x++) {
        const id = ZONE_CHARS[r[x]];
        if (!id) {
          errs.push(`zoneRow ${y} col ${x}: bad char '${r[x]}'`);
          break;
        }
        if (id !== 'none') used.add(id);
      }
    });
    for (const id of used) if (!zoneIds.has(id)) errs.push(`zone '${id}' used in zoneRows but has no rule in zones`);
  }
  const rr = layout.rangeMm;
  if (!rr || !(rr[0] < rr[1])) errs.push('rangeMm must be [lo, hi] with lo < hi');
  const sl = layout.startLengthMm;
  if (!sl || !(sl[0] <= sl[1]) || sl[0] <= 0) errs.push('startLengthMm must be [lo, hi] with 0 < lo ≤ hi');
  if (!(layout.weedFraction >= 0 && layout.weedFraction <= 1)) errs.push('weedFraction must be 0..1');
  const lc = layout.leaves?.count;
  if (!lc || !(lc[0] >= 0 && lc[0] <= lc[1])) errs.push('leaves.count must be [lo, hi]');
  const W = w * cellM;
  const H = h * cellM;
  for (const a of layout.leaves?.areas ?? []) {
    if (a.x < 0 || a.y < 0 || a.x + a.w > W + 1e-6 || a.y + a.h > H + 1e-6 || a.w <= 0 || a.h <= 0) errs.push(`leaf area ${JSON.stringify(a)} outside the map`);
  }
  const ids = new Set<string>();
  for (const p of layout.props ?? []) {
    if (ids.has(p.id)) errs.push(`prop id '${p.id}' duplicated`);
    ids.add(p.id);
    const [ex, ey] = propHalfExtents(p);
    if (p.x - ex < -1e-6 || p.y - ey < -1e-6 || p.x + ex > W + 1e-6 || p.y + ey > H + 1e-6) errs.push(`prop '${p.id}' outside bounds`);
    if (p.r === undefined && (p.w === undefined || p.h === undefined)) errs.push(`prop '${p.id}': needs r or w+h`);
  }
  // Feature P9: an optional downhill vector, small (it's a drift, not a landslide).
  const slope = layout.slope;
  if (slope && !(Number.isFinite(slope.x) && Number.isFinite(slope.y) && Math.hypot(slope.x, slope.y) <= 1)) errs.push('slope must be finite with |slope| ≤ 1');
  const s = layout.spawn;
  if (!s) errs.push('missing spawn');
  else if (errs.length === 0) {
    const g = buildGrid(layout, mulberry32(0));
    const i = cellAt(g, s.x, s.y);
    if (i < 0) errs.push('spawn outside the map');
    else if (g.type[i] !== T_GRASS && g.type[i] !== T_PAVED) errs.push(`spawn on '${CELL_TYPES[g.type[i]]}', must be grass or paved`);
    if (g.mowableCount === 0) errs.push('no mowable cells');
  }
  return errs;
}
